import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  Method,
} from '@lambda/commons/apis/axios';
import pRetry, { AbortError } from 'p-retry';
import SSM from '@lambda/commons/apis/ssm';
import logAxios from '@lambda/commons/logAxios';
import { ErrorWithStatus } from '@lambda/commons/returns';
import { Logger } from '@lambda/commons/logging/logger';
import { ReebeloStoreT } from '@lambda/reebelo';
import { omit } from 'lodash';
import {
  AdminProductCreateT,
  AdminProductListItemT,
  AdminProductSearchQueryT,
  AdminProductSearchResultT,
  AdminProductT,
  AvailableVariantT,
  CategoryVariantT,
  CategoryVariantsByCategoryT,
  SetCategoryVariantsT,
  CategoryVariantValuesResultT,
  CollectionCsvUploadT,
  CollectionT,
  CompetitorPriceT,
  CreateSkuDto,
  GetSkusConfigT,
  OfferSortBy,
  OfferSortByDirection,
  OfferT,
  PriceComponentsForPriceT,
  ProductFiltersT,
  ProductT,
  SearchBrandsResultT,
  SearchCategoriesResultT,
  SearchProductsParamsT,
  SearchProductsResponseT,
  SearchVariantLibraryResultT,
  VariantLibraryItemT,
  SearchQueryParamsT,
  SearchQuerySortT,
  SearchSkusResponseT,
  SkuAllocationOfferT,
  SkuExportJobT,
  ProductExportJobT,
  ProductCompatibilityUploadT,
  SkuT,
  TitleConfigT,
  TitleVariantConfigT,
  EffectiveTitleConfigResultT,
  ProductChannelEnum,
  AdminProductDetailT,
  AdminProductUpdateT,
  CategoryT,
  AdminSpecificationsCommandT,
  AdminFeaturesCommandT,
  AdminFaqsCommandT,
  AdminIdentifiersCommandT,
  AdminIdentifiersResultT,
  ProductConfigurationT,
  AdminComparePricesCommandT,
  ComparePriceRowT,
  CompatibleProductT,
  AdminProductVariantsCommandT,
  AdminProductVariantValuesCommandT,
  AdminValueImagesCommandT,
  AdminProductAuditEventsT,
  RelatedSkuT,
  VendorReviewsAggregateT,
  VendorReviewsResponseT,
  CreateInventoryUploadResultT,
  InventoryUploadT,
  SearchInventoryUploadsParamsT,
  SearchInventoryUploadsResultT,
  SearchInventoryUploadLinesParamsT,
  SearchInventoryUploadLinesResultT,
  ProposedSkuSearchQueryT,
  ProposedSkusPageT,
  ProposedSkuDecisionResultT,
} from './types';
import {
  Category,
  CategoryByPkQueryParams,
  CategoryTree,
  CategoryTreeQueryParams,
  ProductCategoryCondition,
  ProductCategoryWarranty,
} from '../category/types';
import { ProductGroup } from '../product-group/types';
import { PRODUCTS_PER_PAGE } from './constants';

export { PRODUCTS_PER_PAGE };

// A variant row upserted inline with a title-config create/update.
type TitleVariantConfigInput = {
  categoryVariantId: string;
  hidden?: boolean | null;
  sequence?: number | null;
  delimiter?: string | null;
  displayValueFormat?: string | null;
  hiddenValueIds?: string[] | null;
};

const isRetriableNetworkError = (err: any): boolean => {
  if (!err) {
    return false;
  }

  const code = err?.code || err?.cause?.code;

  if (
    code === 'ETIMEDOUT' ||
    code === 'ECONNRESET' ||
    code === 'ECONNREFUSED'
  ) {
    return true;
  }

  if (err?.name === 'AggregateError') {
    return true;
  }

  const status = err?.response?.status || err?.status;

  if (status === 500 || status === 502 || status === 503 || status === 504) {
    return true;
  }

  const message: string = err?.message || '';

  return (
    message.includes('socket hang up') ||
    message.includes('ETIMEDOUT') ||
    message.includes('ECONNRESET') ||
    message.includes('Request failed with status code 500')
  );
};

export const SITEMAP_PAGE_SIZE = 1000;
const carriers = ['AT&T', 'Verizon', 'T-Mobile', 'Sprint'];

const CARRIER_VARIANT_ID_PROD = 'var-mermv1whYv83FoVEGwsPjsd';
const CARRIER_VARIANT_ID_STAGING = 'var-meroxfyy0r2l0TAP69dwVCn';
const CARRIER_UNLOCKED_VALUE = 'Fully Unlocked';

// Hide carrier-locked SKUs by injecting a carrier=Fully Unlocked filter.
const applyCarrierLockFilter = (
  search: SearchProductsParamsT,
  store: ReebeloStoreT,
): SearchProductsParamsT => {
  const isUsSmartphones =
    ['reebelo-us', 'reebelo-dev'].includes(store) &&
    search?.categories?.includes('Smartphones');

  if (!isUsSmartphones) {
    return search;
  }

  const CARRIER_VARIANT_ID =
    store === 'reebelo-us'
      ? CARRIER_VARIANT_ID_PROD
      : CARRIER_VARIANT_ID_STAGING;

  return {
    ...search,
    variants: {
      ...(search.variants ?? {}),
      [CARRIER_VARIANT_ID]: [CARRIER_UNLOCKED_VALUE],
    },
  };
};

/**
 * Serializes array query params as a repeated key — `categoryIds=a&categoryIds=b` — which
 * is what NestJS parses into an array. Axios defaults to `categoryIds[]=a`, and an
 * endpoint using forbidNonWhitelisted rejects that as an unknown parameter.
 */
export const repeatArrayParams = (params: Record<string, unknown>): string => {
  const pairs: [string, string][] = [];

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    (Array.isArray(value) ? value : [value]).forEach((entry) =>
      pairs.push([key, String(entry)]),
    );
  });

  return new URLSearchParams(pairs).toString();
};

export default class CatalogV2Api {
  private api: AxiosInstance;

  protected logger: Logger;

  private ssm: SSM;

  private url = '';

  private apiKey = '';

  private store: ReebeloStoreT;

  // Default product channel applied to every title-bearing read when a call
  // does not pass its own. Lets a consumer (e.g. the storefront) pin a channel
  // once at construction instead of threading it through every call site.
  private defaultChannel?: ProductChannelEnum;

  constructor(
    store: ReebeloStoreT,
    options?: { channel?: ProductChannelEnum },
  ) {
    this.logger = new Logger('ms-catalog-v2');
    this.ssm = new SSM();
    this.store = store;
    this.defaultChannel = options?.channel;
    this.api = axios.create({
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });

    logAxios(this.api, this.logger);
  }

  // Get Products
  async getProducts(
    search: SearchProductsParamsT,
  ): Promise<{ items: ProductT[]; total: number; filters: ProductFiltersT }> {
    try {
      const filteredSearch = applyCarrierLockFilter(search, this.store);
      const searchParams = {
        store: this.store,
        ...(this.defaultChannel ? { channel: this.defaultChannel } : {}),
        limit: PRODUCTS_PER_PAGE,
        offset: 0,
        sortBy: OfferSortBy.SALES,
        sortDirection: OfferSortByDirection.DESC,
        includeFilters: true,
        // The above are default params, can be overridden by search
        // Spread the search object but omit variants to handle them separately
        ...omit(filteredSearch, ['variants']),
        // Add variants in the format variants[key]=value
        ...(filteredSearch.variants
          ? Object.entries(filteredSearch.variants).reduce(
              (acc, [key, value]) => ({
                ...acc,
                [`variants[${key}]`]: value,
              }),
              {},
            )
          : {}),
      };

      this.logger.debug({ searchParams }, 'Fetching products from MsCatalog2');

      return this.makeApiRequest('GET', `/v2/products`, {}, searchParams);
    } catch (err) {
      this.logger.error(
        { err, search },
        'Error fetching products from MsCatalog2',
      );
      throw err;
    }
  }

  // Get New Product Recommendations
  async getNewRecommendations(
    psku: string,
    excludeProducts: string[] = [],
  ): Promise<{ items: SkuT[]; total: number; filters: ProductFiltersT }> {
    try {
      const searchParams: {
        store: ReebeloStoreT;
        excludeProducts?: string[];
      } = {
        store: this.store,
        ...(this.defaultChannel ? { channel: this.defaultChannel } : {}),
      };

      if (excludeProducts.length) {
        searchParams.excludeProducts = excludeProducts;
      }

      this.logger.info(
        { psku, excludeProducts },
        'Fetching new product recommendations from MsCatalog2',
      );

      return this.makeApiRequest(
        'GET',
        `/v2/recommendations/${psku}`,
        {},
        searchParams,
      );
    } catch (err) {
      this.logger.error(
        { err, psku, excludeProducts },
        'Error fetching new product recommendations from MsCatalog2',
      );
      throw err;
    }
  }

  /**
   * The SKUs compatible with any of these products ("More accessories"): one
   * per compatible product, carrying its cheapest in-stock offer in this store,
   * ordered by best sellers. The input products are never included. The
   * endpoint rejects unknown query params, so no channel is sent, and needs
   * `productIds` repeated rather than bracketed.
   */
  async getCompatibleSkus(
    productIds: string[],
    { limit, offset }: { limit?: number; offset?: number } = {},
  ): Promise<{ items: SkuT[]; total: number }> {
    try {
      return await this.makeApiRequest(
        'GET',
        '/v2/products/compatibility',
        {},
        {
          productIds,
          store: this.store,
          ...(limit != null ? { limit } : {}),
          ...(offset != null ? { offset } : {}),
        },
        { paramsSerializer: repeatArrayParams },
      );
    } catch (err) {
      this.logger.error(
        { err, productIds },
        'Error fetching compatible SKUs from MsCatalog2',
      );
      throw err;
    }
  }

  // Get Product Recommendations
  async getProductRecommendations(
    psku: string,
  ): Promise<{ items: ProductT[]; total: number; filters: ProductFiltersT }> {
    try {
      const searchParams = {
        store: this.store,
        ...(this.defaultChannel ? { channel: this.defaultChannel } : {}),
        limit: PRODUCTS_PER_PAGE,
        offset: 0,
        sortBy: OfferSortBy.SALES,
        sortDirection: OfferSortByDirection.DESC,
        includeFilters: false,
      };

      this.logger.info(
        { psku },
        'Fetching product recommendations from MsCatalog2',
      );

      return this.makeApiRequest(
        'GET',
        `/v2/products/${psku}/recommendations`,
        {},
        searchParams,
      );
    } catch (err) {
      this.logger.error(
        { err, psku },
        'Error fetching product recommendations from MsCatalog2',
      );
      throw err;
    }
  }

  // Get SKUs
  async getSkus(
    search: SearchProductsParamsT,
  ): Promise<{ items: SkuT[]; total: number }> {
    try {
      const searchParams = {
        store: this.store,
        ...(this.defaultChannel ? { channel: this.defaultChannel } : {}),
        limit: PRODUCTS_PER_PAGE,
        offset: 0,
        sortBy: OfferSortBy.SALES,
        sortDirection: OfferSortByDirection.DESC,
        includeFilters: false,
        // The above are default params, can be overridden by search
        // Spread the search object but omit variants to handle them separately
        ...omit(search, ['variants']),
        // Add variants in the format variants[key]=value
        ...(search.variants
          ? Object.entries(search.variants).reduce(
              (acc, [key, value]) => ({
                ...acc,
                [`variants[${key}]`]: value,
              }),
              {},
            )
          : {}),
      };

      this.logger.debug({ searchParams }, 'Fetching SKUs from MsCatalog2');

      return this.makeApiRequest('GET', `/v2/skus`, {}, searchParams);
    } catch (err) {
      this.logger.error({ err, search }, 'Error fetching SKUs from MsCatalog2');
      throw err;
    }
  }

  // Get Product By Product Id
  async getProductByProductId(
    productId: string,
    options?: { bustCache?: boolean; channel?: ProductChannelEnum },
  ): Promise<ProductT | null> {
    try {
      this.logger.info(
        { productId, store: this.store },
        'Fetching product from MsCatalog2',
      );

      // A 404 is an answer here, not a failure: this returns `ProductT | null`, and the
      // storefront calls it to ask "is this handle a product or a collection?" — so every
      // collection page view logged an ERROR for a handle that was never a product. That
      // noise is why real failures got missed in Datadog.
      return this.makeApiRequest(
        'GET',
        `/v2/products/${productId}`,
        {},
        {
          store: this.store,
          offerCount: 0,
          bustCache: options?.bustCache === true ? options.bustCache : false,
          channel: options?.channel ?? this.defaultChannel,
        },
        { expectedStatuses: [404] },
      );
    } catch (err) {
      this.logger.error(
        { err, productId, options },
        'Error fetching product from MsCatalog2',
      );
      throw err;
    }
  }

  // Get Product SKUs
  async getProductSkus(
    psku: string,
    config: GetSkusConfigT,
  ): Promise<{ items: SkuT[]; count: number }> {
    try {
      return this.makeApiRequest(
        'GET',
        `/v2/products/${psku}/skus`,
        {},
        { channel: this.defaultChannel, ...config },
      );
    } catch (err) {
      this.logger.error(
        { err, psku },
        'Error fetching product SKUs from MsCatalog2',
      );
      throw err;
    }
  }

  // Get Available Product Variants
  async getAvailableVariants(
    productId: string,
  ): Promise<{ items: AvailableVariantT[] }> {
    try {
      return this.makeApiRequest(
        'GET',
        `/v2/products/${productId}/available-skus`,
        {},
        { store: this.store },
      );
    } catch (err) {
      this.logger.error(
        { err, productId },
        'Error fetching available product SKUs from MsCatalog2',
      );
      throw err;
    }
  }

  // Get a page of a vendor's reviews
  async getVendorReviews({
    vendorName,
    limit,
    offset,
  }: {
    vendorName: string;
    limit: number;
    offset: number;
  }): Promise<VendorReviewsResponseT | null> {
    try {
      return await this.makeApiRequest(
        'GET',
        `/v2/reviews`,
        {},
        {
          store: this.store,
          // Vendors are keyed by `<store>#<vendor>` on this route.
          vendorId: `${this.store}#${vendorName}`,
          limit,
          offset,
        },
      );
    } catch (err) {
      this.logger.error(
        { err, vendorName, limit, offset },
        'Error fetching vendor reviews from MsCatalog2',
      );
      throw err;
    }
  }

  /**
   * A page of a product's reviews.
   *
   * Reviews are recorded per SKU, so `psku` widens to every SKU under the
   * product. `minReview` keeps parity with the S3 pipeline this replaces,
   * which only ever published reviews above 2 stars.
   */
  async getProductReviews({
    psku,
    limit,
    offset,
    minReview,
  }: {
    psku: string;
    limit: number;
    offset: number;
    minReview?: number;
  }): Promise<VendorReviewsResponseT | null> {
    try {
      return await this.makeApiRequest(
        'GET',
        `/v2/reviews`,
        {},
        { store: this.store, psku, limit, offset, minReview },
      );
    } catch (err) {
      this.logger.error(
        { err, psku, limit, offset },
        'Error fetching product reviews from MsCatalog2',
      );
      throw err;
    }
  }

  /**
   * A product's aggregated review figures.
   *
   * Deliberately unfiltered by rating: the star breakdown on the product page
   * has always been computed over every review, including the one- and
   * two-star ones the list itself leaves out.
   */
  async getProductReviewsAggregate(
    psku: string,
  ): Promise<VendorReviewsAggregateT | null> {
    try {
      return await this.makeApiRequest(
        'GET',
        `/v2/reviews/aggregate`,
        {},
        { store: this.store, psku },
      );
    } catch (err) {
      this.logger.error(
        { err, psku },
        'Error fetching product reviews aggregate from MsCatalog2',
      );
      throw err;
    }
  }

  // Get a vendor's aggregated review figures
  async getVendorReviewsAggregate(
    vendorName: string,
  ): Promise<VendorReviewsAggregateT | null> {
    try {
      return await this.makeApiRequest(
        'GET',
        `/v2/reviews/aggregate`,
        {},
        {
          store: this.store,
          // Vendors are keyed by `<store>#<vendor>` on this route.
          vendorId: `${this.store}#${vendorName}`,
        },
      );
    } catch (err) {
      this.logger.error(
        { err, vendorName },
        'Error fetching vendor reviews aggregate from MsCatalog2',
      );
      throw err;
    }
  }

  async getCategoryTree({
    sortBy,
    sortDirection,
    store,
  }: CategoryTreeQueryParams = {}): Promise<{ categories: CategoryTree[] }> {
    try {
      return await this.makeApiRequest(
        'GET',
        `/v2/categories`,
        {},
        { sortBy, sortDirection, store },
      );
    } catch (err) {
      this.logger.error(
        { err, sortBy, sortDirection, store },
        'Error fetching category tree from MsCatalog2',
      );
      throw err;
    }
  }

  async getCategoryWarranty(
    categoryPk: string,
  ): Promise<ProductCategoryWarranty | null> {
    try {
      return await this.makeApiRequest(
        'GET',
        `/v2/categories/${categoryPk}/warranty`,
        {},
        { store: this.store },
      );
    } catch (err) {
      this.logger.error(
        { err, categoryPk, store: this.store },
        'Error fetching category warranty from MsCatalog2',
      );
      throw err;
    }
  }

  async getCategoryBreadcrumb(
    categoryPk: string,
  ): Promise<CategoryTree | null> {
    try {
      return await this.makeApiRequest(
        'GET',
        `/v2/categories/${categoryPk}/parent`,
      );
    } catch (err) {
      this.logger.error(
        { err, categoryPk },
        'Error fetching category breadcrumb from MsCatalog2',
      );
      throw err;
    }
  }

  async getCategoryByPk(
    categoryPk: string,
    { includes }: CategoryByPkQueryParams = {},
  ): Promise<CategoryTree | null> {
    try {
      return await this.makeApiRequest(
        'GET',
        `/v2/categories/${categoryPk}/subtree`,
        {},
        { store: this.store, includes },
      );
    } catch (err) {
      this.logger.error(
        { err, categoryPk, store: this.store, includes },
        'Error fetching category by pk from MsCatalog2',
      );
      throw err;
    }
  }

  async getGroupByHandle(handle: string): Promise<ProductGroup | null> {
    try {
      return await this.makeApiRequest(
        'GET',
        `/v2/groups/handle/${handle}`,
        {},
        { store: this.store },
      );
    } catch (err) {
      this.logger.error(
        { err, handle, store: this.store },
        'Error fetching group by handle from MsCatalog2',
      );
      throw err;
    }
  }

  async getBasicCategoryByPk(categoryPk: string): Promise<Category | null> {
    try {
      return await this.makeApiRequest('GET', `/v2/categories/${categoryPk}`);
    } catch (err) {
      this.logger.error(
        { err, categoryPk, store: this.store },
        'Error fetching basic category by pk from MsCatalog2',
      );
      throw err;
    }
  }

  async createCategory(body: {
    name: string;
    description?: string;
    parent?: string;
  }): Promise<Category | null> {
    try {
      return await this.makeApiRequest('POST', `/v2/categories`, body);
    } catch (err) {
      this.logger.error({ err, body }, 'Error creating category in MsCatalog2');
      throw err;
    }
  }

  async updateCategory(
    categoryPk: string,
    body: { name?: string; parent?: string },
  ): Promise<Category | null> {
    try {
      return await this.makeApiRequest(
        'PATCH',
        `/v2/categories/${categoryPk}`,
        body,
      );
    } catch (err) {
      this.logger.error(
        { err, categoryPk, body },
        'Error updating category in MsCatalog2',
      );
      throw err;
    }
  }

  async deleteCategory(categoryPk: string): Promise<void> {
    try {
      await this.makeApiRequest('DELETE', `/v2/categories/${categoryPk}`);
    } catch (err) {
      this.logger.error(
        { err, categoryPk },
        'Error deleting category in MsCatalog2',
      );
      throw err;
    }
  }

  async getSkuById(skuId: string, channel?: string): Promise<SkuT | null> {
    try {
      return this.makeApiRequest(
        'GET',
        `/v2/skus/${skuId}`,
        {},
        { store: this.store, channel: channel ?? this.defaultChannel },
      );
    } catch (err) {
      this.logger.error(
        { err, skuId, channel },
        'Error fetching SKU from MsCatalog2',
      );
      throw err;
    }
  }

  async getCategoryConditions(
    categoryId: string,
  ): Promise<ProductCategoryCondition | null> {
    try {
      return await this.makeApiRequest(
        'GET',
        `/v2/categories/${categoryId}/conditions`,
        {},
        { store: this.store },
      );
    } catch (err) {
      this.logger.error(
        { err, categoryId, store: this.store },
        'Error fetching category conditions from MsCatalog2',
      );
      throw err;
    }
  }

  async getOffersByIds(offerIds: string[]): Promise<OfferT[] | null> {
    try {
      const response = await this.makeApiRequest<{
        items: OfferT[];
        total: number;
      }>('GET', `/v2/offers/batch`, {}, { offerIds });

      this.logger.info(
        { offerIds, response },
        'Fetched offers by IDs from MsCatalog2',
      );

      return response?.items ?? [];
    } catch (err) {
      this.logger.error(
        { err, offerIds },
        'Error fetching offers from MsCatalog2',
      );

      return [];
    }
  }

  async getOfferByVendorSku(
    vendorId: string,
    vendorSku: string,
  ): Promise<OfferT | null> {
    try {
      const response = await this.makeApiRequest<OfferT>(
        'GET',
        `/v2/offers/vendor-sku`,
        {},
        {
          vendorId,
          vendorSku,
        },
      );

      this.logger.info(
        { vendorId, vendorSku, response },
        'Fetched offer by vendor SKU from MsCatalog2',
      );

      return response || null;
    } catch (err) {
      this.logger.error(
        { err, vendorId, vendorSku },
        'Error fetching offer by vendor SKU from MsCatalog2',
      );

      return null;
    }
  }

  // Get Deals
  async getDeals(
    search: SearchProductsParamsT,
  ): Promise<{ items: SkuT[]; total: number; filters: ProductFiltersT }> {
    try {
      const searchParams = {
        store: this.store,
        ...(this.defaultChannel ? { channel: this.defaultChannel } : {}),
        limit: PRODUCTS_PER_PAGE,
        offset: 0,
        sortBy: OfferSortBy.SALES,
        sortDirection: OfferSortByDirection.DESC,
        includeFilters: true,
        // The above are default params, can be overridden by search
        // Spread the search object but omit variants to handle them separately
        ...omit(search, ['variants']),
        // Add variants in the format variants[key]=value
        ...(search.variants
          ? Object.entries(search.variants).reduce(
              (acc, [key, value]) => ({
                ...acc,
                [`variants[${key}]`]: value,
              }),
              {},
            )
          : {}),
      };

      this.logger.debug({ searchParams }, 'Fetching Deals from MsCatalog2');

      const response = await this.makeApiRequest<{
        items: SkuT[];
        total: number;
        filters: ProductFiltersT;
      }>('GET', `/v2/deals`, {}, searchParams);

      if (response?.items?.length > 0) {
        response.items = response.items.filter(
          (sku) => !carriers.some((carrier) => sku?.title?.includes(carrier)),
        );
      }

      return response || { items: [], total: 0, filters: {} };
    } catch (err) {
      this.logger.error(
        { err, search },
        'Error fetching Deals from MsCatalog2',
      );
      throw err;
    }
  }

  // Search SKUs — /v2/search/skus
  async getSearchSkus(
    params: SearchQueryParamsT,
    options: { signal?: AbortSignal; timeout?: number } = {},
  ): Promise<SearchSkusResponseT> {
    try {
      const searchParams = {
        store: this.store,
        limit: 24,
        offset: 0,
        sort: SearchQuerySortT.RELEVANCE,
        includeFilters: true,
        ...omit(params, ['variants']),
        ...(params.variants
          ? Object.entries(params.variants).reduce(
              (acc, [key, value]) => ({
                ...acc,
                [`variants[${key}]`]: value,
              }),
              {},
            )
          : {}),
      };

      this.logger.info(
        {
          q_len: params.q.length,
          store: searchParams.store,
          sort: searchParams.sort,
          limit: searchParams.limit,
        },
        'Fetching SKU search results from MsCatalog2',
      );

      return this.makeApiRequest(
        'GET',
        `/v2/search/skus`,
        {},
        searchParams,
        options,
      );
    } catch (err) {
      this.logger.error(
        { err, store: this.store, q_len: params.q.length },
        'Error fetching SKU search results from MsCatalog2',
      );
      throw err;
    }
  }

  // Search Products — /v2/search/products
  async getSearchProducts(
    params: SearchQueryParamsT,
    options: { signal?: AbortSignal; timeout?: number } = {},
  ): Promise<SearchProductsResponseT> {
    try {
      const searchParams = {
        store: this.store,
        limit: 24,
        offset: 0,
        sort: SearchQuerySortT.RELEVANCE,
        includeFilters: true,
        ...omit(params, ['variants']),
        ...(params.variants
          ? Object.entries(params.variants).reduce(
              (acc, [key, value]) => ({
                ...acc,
                [`variants[${key}]`]: value,
              }),
              {},
            )
          : {}),
      };

      this.logger.info(
        {
          q_len: params.q.length,
          store: searchParams.store,
          sort: searchParams.sort,
          limit: searchParams.limit,
        },
        'Fetching product search results from MsCatalog2',
      );

      return this.makeApiRequest(
        'GET',
        `/v2/search/products`,
        {},
        searchParams,
        options,
      );
    } catch (err) {
      this.logger.error(
        { err, store: this.store, q_len: params.q.length },
        'Error fetching product search results from MsCatalog2',
      );
      throw err;
    }
  }

  // Get Deals
  async getSitemap({
    limit,
    offset,
  }: {
    limit: number;
    offset: number;
  }): Promise<{
    items: { slug: string; lastModified: string }[];
    total: number;
  }> {
    try {
      this.logger.debug('Fetching Products Sitemap from MsCatalog2');

      return this.makeApiRequest(
        'GET',
        `/v2/sitemap`,
        {},
        { store: this.store, limit, offset },
      );
    } catch (err) {
      this.logger.error(
        { err },
        'Error fetching Products Sitemap from MsCatalog2',
      );
      throw err;
    }
  }

  // Get Featured Deals
  async getFeaturedDeals(
    exclude: Pick<SearchProductsParamsT, 'excludeCategories'> = {},
  ): Promise<{ items: SkuT[]; total: number }> {
    try {
      const searchParams = {
        store: this.store,
        ...(this.defaultChannel ? { channel: this.defaultChannel } : {}),
        ...exclude,
      };

      this.logger.info(
        { searchParams },
        'Fetching Featured Deals from MsCatalog2',
      );

      const response = await this.makeApiRequest<{
        items: SkuT[];
        total: number;
      }>('GET', `/v2/deals/featured`, {}, searchParams);

      if (response?.items?.length > 0) {
        response.items = response.items.filter(
          (sku) => !carriers.some((carrier) => sku?.title?.includes(carrier)),
        );
      }

      return response;
    } catch (err) {
      this.logger.error(
        { err },
        'Error fetching Featured Deals from MsCatalog2',
      );
      throw err;
    }
  }

  async createSku(createSkuDto: CreateSkuDto): Promise<SkuT | null> {
    const createSkuRequest = async () => {
      try {
        return await this.makeApiRequest<SkuT>(
          'POST',
          `/v2/skus`,
          createSkuDto,
        );
      } catch (err) {
        if (!isRetriableNetworkError(err)) {
          throw new AbortError(err as Error);
        }
        throw err;
      }
    };

    try {
      return await pRetry(createSkuRequest, {
        retries: 3,
        factor: 1,
        minTimeout: 3000,
        maxTimeout: 3000,
        randomize: false,
        onFailedAttempt: (error) => {
          this.logger.warn(
            {
              attempt: error.attemptNumber,
              retriesLeft: error.retriesLeft,
              message: error.message,
              createSkuDto,
            },
            'createSku: transient failure, retrying...',
          );
        },
      });
    } catch (err) {
      this.logger.error(
        { err, createSkuDto },
        'Error creating SKU in MsCatalog2',
      );
      throw err;
    }
  }

  async updateCompetitorPrice(rsku: string): Promise<void> {
    try {
      await this.makeApiRequest('POST', `/v1/legacy/competitor-offers`, {
        rsku,
      });
    } catch (err) {
      this.logger.error(
        { err, rsku },
        'Error updating competitor price in MsCatalog2',
      );
    }
  }

  getAdminSkus(skuIds: string[]): Promise<{ items: SkuT[] }> {
    try {
      return this.makeApiRequest(
        'GET',
        '/v1/admin/skus',
        {},
        {
          store: this.store,
          skuIds,
        },
      );
    } catch (err) {
      this.logger.error(
        {
          err,
          store: this.store,
          skuIds,
        },
        'Error fetching admin skus',
      );
      throw err;
    }
  }

  /**
   * `stores` widens the search for a seller cross-listed across markets; each
   * row then says which market its price came from.
   */
  getAdminSkuCompetitors(
    skuIds: string[],
    stores?: ReebeloStoreT[],
  ): Promise<{ items: CompetitorPriceT[] }> {
    try {
      return this.makeApiRequest(
        'GET',
        '/v1/admin/skus/competitors',
        {},
        {
          store: this.store,
          ...(stores?.length ? { stores } : {}),
          skuIds,
        },
      );
    } catch (err) {
      this.logger.error(
        {
          err,
          store: this.store,
          skuIds,
        },
        'Error fetching admin competitors',
      );
      throw err;
    }
  }

  getRelatedSkus(skuId: string): Promise<RelatedSkuT[]> {
    try {
      return this.makeApiRequest(
        'GET',
        `/v2/skus/${skuId}/related`,
        {},
        { store: this.store },
      );
    } catch (err) {
      this.logger.error(
        {
          err,
          store: this.store,
          skuId,
        },
        'Error fetching related skus',
      );
      throw err;
    }
  }

  getSkuOfferAllocation(
    skuId: string,
    quantity = 1,
  ): Promise<{ items: OfferT[]; availableQuantity: number }> {
    try {
      return this.makeApiRequest(
        'GET',
        `/v2/skus/${skuId}/offer-allocation`,
        {},
        { store: this.store, quantity },
      );
    } catch (err) {
      this.logger.error(
        {
          err,
          store: this.store,
          skuId,
          quantity,
        },
        'Error fetching sku offer allocation',
      );
      throw err;
    }
  }

  async getPriceComponents(
    prices: number[],
  ): Promise<{ items: PriceComponentsForPriceT[] }> {
    try {
      return await this.makeApiRequest(
        'POST',
        '/v2/price-components/calculate',
        {
          store: this.store,
          prices,
        },
      );
    } catch (err) {
      this.logger.error(
        {
          err,
          store: this.store,
          prices,
        },
        'Error fetching price components',
      );
      throw err;
    }
  }

  getAdminProducts({
    psku,
  }: {
    psku?: string;
  }): Promise<AdminProductSearchResultT> {
    try {
      return this.makeApiRequest('GET', '/v1/admin/products', {}, { psku });
    } catch (err) {
      this.logger.error(
        {
          err,
          psku,
        },
        'Error fetching admin products',
      );
      throw err;
    }
  }

  /**
   * The admin product listing. Unlike every other product search in this client, this one
   * does not require an in-stock offer, so it returns product that cannot be sold yet.
   *
   * The serializer matters: axios would otherwise send `categoryIds[]=cat-1`, and the
   * endpoint validates with forbidNonWhitelisted, so the bracketed name comes back as a
   * 400 rather than as a filter.
   */
  async adminSearchProducts(
    query: AdminProductSearchQueryT,
  ): Promise<AdminProductSearchResultT> {
    try {
      return await this.makeApiRequest('GET', '/v1/admin/products', {}, query, {
        paramsSerializer: repeatArrayParams,
      });
    } catch (err) {
      this.logger.error({ err, query }, 'Error searching admin products');
      throw err;
    }
  }

  /**
   * Creates a product.
   *
   * Three 409s are possible, distinguished by a `code` in the body: PSKU_IN_USE and
   * SLUG_IN_USE are refusals, while PSKU_DELETED means a soft-deleted product holds that
   * PSKU and `confirm` restores it.
   */
  async adminCreateProduct(
    body: AdminProductCreateT,
    confirm = false,
  ): Promise<AdminProductDetailT> {
    try {
      return await this.makeApiRequest(
        'POST',
        '/v1/admin/products',
        body,
        confirm ? { confirm: 'true' } : {},
      );
    } catch (err) {
      this.logger.error({ err, body, confirm }, 'Error creating admin product');
      throw err;
    }
  }

  async adminCreateCollection(body: {
    store: ReebeloStoreT;
    productId?: string;
    categoryId?: string;
    position: number;
    description: string;
    skuIds?: string[];
  }): Promise<Pick<CollectionT, 'id'>> {
    try {
      return await this.makeApiRequest('POST', '/v1/admin/collections', {
        store: body.store,
        productId: body.productId,
        categoryId: body.categoryId,
        position: body.position,
        description: body.description,
        skuIds: body.skuIds,
      });
    } catch (err) {
      this.logger.error({ err, body }, 'Error creating collection');
      throw err;
    }
  }

  async adminGetCollections(query: {
    q?: string;
    store?: ReebeloStoreT;
    categoryId?: string;
    brandId?: string;
  }): Promise<{ items: CollectionT[] }> {
    try {
      return await this.makeApiRequest(
        'GET',
        '/v1/admin/collections',
        {},
        query,
      );
    } catch (err) {
      this.logger.error({ err, query }, 'Error getting collections');
      throw err;
    }
  }

  async adminGetCategoryCollections(query: {
    name?: string;
    store?: ReebeloStoreT;
  }): Promise<{ items: CollectionT[] }> {
    try {
      return await this.makeApiRequest(
        'GET',
        '/v1/admin/collections/categories',
        {},
        query,
      );
    } catch (err) {
      this.logger.error({ err, query }, 'Error getting collections');
      throw err;
    }
  }

  async adminGetCollection(id: string): Promise<CollectionT> {
    try {
      return await this.makeApiRequest('GET', `/v1/admin/collections/${id}`);
    } catch (err) {
      this.logger.error({ err, id }, 'Error getting collection');
      throw err;
    }
  }

  async adminUpdateCollection(
    id: string,
    body: {
      description?: string;
      skuIds?: string[];
    },
  ): Promise<CollectionT> {
    try {
      return await this.makeApiRequest('PATCH', `/v1/admin/collections/${id}`, {
        description: body.description,
        skuIds: body.skuIds,
      });
    } catch (err) {
      this.logger.error({ err, id, body }, 'Error updating collection');
      throw err;
    }
  }

  async adminDeleteCollection(id: string): Promise<void> {
    try {
      return await this.makeApiRequest('DELETE', `/v1/admin/collections/${id}`);
    } catch (err) {
      this.logger.error({ err, id }, 'Error updating collection');
      throw err;
    }
  }

  async adminCreateCollectionsCsvUpload(body: {
    originalFileName?: string;
    uploadedBy?: string;
  }): Promise<{ uploadId: string; url: string }> {
    try {
      return await this.makeApiRequest(
        'POST',
        '/v1/admin/collections/uploads',
        body,
      );
    } catch (err) {
      this.logger.error({ err, body }, 'Error creating collections CSV upload');
      throw err;
    }
  }

  async adminGetCollectionsCsvUpload(
    id: string,
  ): Promise<CollectionCsvUploadT> {
    try {
      return await this.makeApiRequest(
        'GET',
        `/v1/admin/collections/uploads/${id}`,
      );
    } catch (err) {
      this.logger.error({ err, id }, 'Error getting collections CSV upload');
      throw err;
    }
  }

  async adminCreateSkuExportJob(body: {
    store: string;
    categoryId: string;
    brandId: string;
  }): Promise<{ jobId: string }> {
    try {
      return await this.makeApiRequest('POST', '/v1/exports/jobs', body);
    } catch (err) {
      this.logger.error({ err, body }, 'Error creating SKU export job');
      throw err;
    }
  }

  async adminGetSkuExportJob(id: string): Promise<SkuExportJobT> {
    try {
      return await this.makeApiRequest('GET', `/v1/exports/jobs/${id}`);
    } catch (err) {
      this.logger.error({ err, id }, 'Error getting SKU export job');
      throw err;
    }
  }

  async adminCreateProductExportJob(body: {
    store: string;
    q?: string;
    categoryIds?: string[];
    brandIds?: string[];
  }): Promise<{ jobId: string }> {
    try {
      return await this.makeApiRequest(
        'POST',
        '/v1/exports/products/jobs',
        body,
      );
    } catch (err) {
      this.logger.error({ err, body }, 'Error creating product export job');
      throw err;
    }
  }

  async adminGetProductExportJob(id: string): Promise<ProductExportJobT> {
    try {
      return await this.makeApiRequest(
        'GET',
        `/v1/exports/products/jobs/${id}`,
      );
    } catch (err) {
      this.logger.error({ err, id }, 'Error getting product export job');
      throw err;
    }
  }

  async adminCreateGtinExportJob(body: {
    store: string;
    q?: string;
    categoryIds?: string[];
    brandIds?: string[];
  }): Promise<{ jobId: string }> {
    try {
      return await this.makeApiRequest('POST', '/v1/exports/gtins/jobs', body);
    } catch (err) {
      this.logger.error({ err, body }, 'Error creating GTIN export job');
      throw err;
    }
  }

  async adminGetGtinExportJob(id: string): Promise<ProductExportJobT> {
    try {
      return await this.makeApiRequest('GET', `/v1/exports/gtins/jobs/${id}`);
    } catch (err) {
      this.logger.error({ err, id }, 'Error getting GTIN export job');
      throw err;
    }
  }

  async adminCreateTemplateExportJob(body: {
    store: string;
    categoryId: string;
    brandId: string;
  }): Promise<{ jobId: string }> {
    try {
      return await this.makeApiRequest(
        'POST',
        '/v1/exports/templates/jobs',
        body,
      );
    } catch (err) {
      this.logger.error({ err, body }, 'Error creating template export job');
      throw err;
    }
  }

  async adminGetTemplateExportJob(id: string): Promise<ProductExportJobT> {
    try {
      return await this.makeApiRequest(
        'GET',
        `/v1/exports/templates/jobs/${id}`,
      );
    } catch (err) {
      this.logger.error({ err, id }, 'Error getting template export job');
      throw err;
    }
  }

  async adminSearchProposedSkus(
    query: ProposedSkuSearchQueryT,
  ): Promise<ProposedSkusPageT> {
    try {
      return await this.makeApiRequest(
        'GET',
        '/v1/admin/proposed-skus',
        {},
        query,
        { paramsSerializer: repeatArrayParams },
      );
    } catch (err) {
      this.logger.error({ err, query }, 'Error searching proposed SKUs');
      throw err;
    }
  }

  async adminApproveProposedSkus(
    ids: string[],
  ): Promise<ProposedSkuDecisionResultT> {
    try {
      return await this.makeApiRequest(
        'POST',
        '/v1/admin/proposed-skus/approval',
        { ids },
      );
    } catch (err) {
      this.logger.error({ err, ids }, 'Error approving proposed SKUs');
      throw err;
    }
  }

  async adminRejectProposedSkus(
    ids: string[],
    reason?: string,
  ): Promise<ProposedSkuDecisionResultT> {
    try {
      return await this.makeApiRequest(
        'POST',
        '/v1/admin/proposed-skus/rejection',
        { ids, reason },
      );
    } catch (err) {
      this.logger.error({ err, ids }, 'Error rejecting proposed SKUs');
      throw err;
    }
  }

  async adminSearchCategories(query: {
    name?: string;
    limit?: number;
    offset?: number;
  }): Promise<SearchCategoriesResultT> {
    try {
      return await this.makeApiRequest(
        'GET',
        '/v1/admin/categories',
        {},
        query,
      );
    } catch (err) {
      this.logger.error({ err, query }, 'Error searching categories');
      throw err;
    }
  }

  // The category's own variant pairings. Nothing is inherited from ancestors.
  async adminListCategoryVariants(
    categoryId: string,
  ): Promise<CategoryVariantT[]> {
    try {
      return await this.makeApiRequest(
        'GET',
        `/v1/admin/categories/${categoryId}/variants`,
      );
    } catch (err) {
      this.logger.error({ err, categoryId }, 'Error listing category variants');
      throw err;
    }
  }

  // Every category's pairings in one call — the bulk read behind the legacy
  // `GET /product-category-variants` list-all. Its own top-level resource, separate from
  // the per-category `:id/variants` route above.
  async adminListAllCategoryVariants(): Promise<CategoryVariantsByCategoryT[]> {
    try {
      return await this.makeApiRequest('GET', '/v1/admin/category-variants');
    } catch (err) {
      this.logger.error({ err }, 'Error listing all category variants');
      throw err;
    }
  }

  // Bulk replace of a category's variants + values in one PUT — the legacy
  // `PUT /product-category-variants/:pk` write. Applied additively server-side (attach
  // and add only); removals stay with the value editor / guarded detach flow.
  async adminSetCategoryVariants(
    categoryId: string,
    body: SetCategoryVariantsT,
  ): Promise<CategoryVariantT[]> {
    try {
      return await this.makeApiRequest(
        'PUT',
        `/v1/admin/categories/${categoryId}/variants`,
        body,
      );
    } catch (err) {
      this.logger.error(
        { err, categoryId },
        'Error bulk-setting category variants',
      );
      throw err;
    }
  }

  // The attach flow's library search. `excludeCategoryId` hides types the category
  // already has, so the modal only offers something new.
  async adminSearchVariantLibrary(query: {
    excludeCategoryId?: string;
    name?: string;
    limit?: number;
    offset?: number;
  }): Promise<SearchVariantLibraryResultT> {
    try {
      return await this.makeApiRequest('GET', '/v1/admin/variants', {}, query);
    } catch (err) {
      this.logger.error({ err, query }, 'Error searching the variant library');
      throw err;
    }
  }

  async adminCreateVariant(name: string): Promise<VariantLibraryItemT> {
    try {
      return await this.makeApiRequest('POST', '/v1/admin/variants', { name });
    } catch (err) {
      this.logger.error({ err, name }, 'Error creating a variant');
      throw err;
    }
  }

  async adminUpdateVariant(
    variantId: string,
    body: { name?: string; hidden?: boolean },
  ): Promise<VariantLibraryItemT> {
    try {
      return await this.makeApiRequest(
        'PATCH',
        `/v1/admin/variants/${variantId}`,
        body,
      );
    } catch (err) {
      this.logger.error({ err, variantId }, 'Error updating a variant');
      throw err;
    }
  }

  async adminDeleteVariant(variantId: string): Promise<void> {
    try {
      await this.makeApiRequest('DELETE', `/v1/admin/variants/${variantId}`);
    } catch (err) {
      this.logger.error({ err, variantId }, 'Error deleting a variant');
      throw err;
    }
  }

  // Attach one variant to many categories. Categories already carrying it are skipped
  // server-side, so sending a whole branch is safe.
  async adminAttachVariantCategories(
    variantId: string,
    categoryIds: string[],
  ): Promise<VariantLibraryItemT> {
    try {
      return await this.makeApiRequest(
        'PATCH',
        `/v1/admin/variants/${variantId}/categories`,
        { categoryIds },
      );
    } catch (err) {
      this.logger.error(
        { err, variantId },
        'Error attaching a variant to categories',
      );
      throw err;
    }
  }

  // Attach, detach, reorder and flag updates go through one endpoint so each does not
  // need its own deployed lambda. Exactly one command is accepted per call; the refreshed
  // list comes back, which is what the caller renders next.
  async adminUpdateCategoryVariants(
    categoryId: string,
    command: {
      attach?: {
        variantId?: string;
        name?: string;
        optional?: boolean;
        values?: string[];
      };
      detach?: { variantId: string; confirm?: boolean };
      order?: string[];
      update?: { variantId: string; optional?: boolean; hidden?: boolean };
    },
  ): Promise<CategoryVariantT[]> {
    try {
      return await this.makeApiRequest(
        'PATCH',
        `/v1/admin/categories/${categoryId}/variants`,
        command,
      );
    } catch (err) {
      this.logger.error(
        { err, categoryId },
        'Error applying a category variant command',
      );
      throw err;
    }
  }

  // ---- Values of one category's variant pairing (the value editor) ----------------

  async adminListCategoryVariantValues(
    categoryId: string,
    variantId: string,
    query: { search?: string; limit?: number; offset?: number } = {},
  ): Promise<CategoryVariantValuesResultT> {
    try {
      return await this.makeApiRequest(
        'GET',
        `/v1/admin/categories/${categoryId}/variants/${variantId}/values`,
        {},
        query,
      );
    } catch (err) {
      this.logger.error(
        { err, categoryId, variantId, query },
        'Error listing category variant values',
      );
      throw err;
    }
  }

  // Create, relabel, delete and move go through one endpoint, as above.
  async adminUpdateCategoryVariantValues(
    categoryId: string,
    variantId: string,
    command: {
      create?: { value: string; displayValue?: string };
      update?: { id: string; displayValue?: string };
      delete?: { id: string; confirm?: boolean };
      move?: { id: string; beforeId?: string };
    },
    query: { search?: string; limit?: number; offset?: number } = {},
  ): Promise<CategoryVariantValuesResultT> {
    try {
      return await this.makeApiRequest(
        'PATCH',
        `/v1/admin/categories/${categoryId}/variants/${variantId}/values`,
        command,
        query,
      );
    } catch (err) {
      this.logger.error(
        { err, categoryId, variantId },
        'Error applying a category variant value command',
      );
      throw err;
    }
  }

  async adminSearchBrands(query: {
    name?: string;
    categoryId?: string;
    limit?: number;
    offset?: number;
  }): Promise<SearchBrandsResultT> {
    try {
      return await this.makeApiRequest('GET', '/v1/admin/brands', {}, query);
    } catch (err) {
      this.logger.error({ err, query }, 'Error searching brands');
      throw err;
    }
  }

  // --- Title config ------------------------------------------

  async adminListTitleConfigs(categoryId: string): Promise<TitleConfigT[]> {
    try {
      return await this.makeApiRequest(
        'GET',
        '/v2/title-configs',
        {},
        { categoryId },
      );
    } catch (err) {
      this.logger.error({ err, categoryId }, 'Error listing title configs');
      throw err;
    }
  }

  async adminGetEffectiveTitleConfig(query: {
    categoryId: string;
    store?: string;
    channel?: string;
  }): Promise<EffectiveTitleConfigResultT> {
    try {
      return await this.makeApiRequest(
        'GET',
        '/v2/title-configs/effective',
        {},
        query,
      );
    } catch (err) {
      this.logger.error({ err, query }, 'Error getting effective title config');
      throw err;
    }
  }

  async adminCreateTitleConfig(body: {
    id?: string;
    categoryId: string;
    name?: string | null;
    stores?: string[];
    channels?: string[];
    precedence?: number;
    titlePrefix?: string | null;
    titleSuffix?: string | null;
    showBrandInTitle?: boolean | null;
    showCategoryInTitle?: boolean | null;
    variants?: TitleVariantConfigInput[];
  }): Promise<TitleConfigT> {
    try {
      return await this.makeApiRequest('POST', '/v2/title-configs', body);
    } catch (err) {
      this.logger.error({ err, body }, 'Error creating title config');
      throw err;
    }
  }

  async adminUpdateTitleConfig(
    id: string,
    body: {
      name?: string | null;
      stores?: string[];
      channels?: string[];
      precedence?: number;
      titlePrefix?: string | null;
      titleSuffix?: string | null;
      showBrandInTitle?: boolean | null;
      showCategoryInTitle?: boolean | null;
      variants?: TitleVariantConfigInput[];
    },
  ): Promise<TitleConfigT> {
    try {
      return await this.makeApiRequest(
        'PATCH',
        `/v2/title-configs/${id}`,
        body,
      );
    } catch (err) {
      this.logger.error({ err, id }, 'Error updating title config');
      throw err;
    }
  }

  async adminDeleteTitleConfig(id: string): Promise<void> {
    try {
      await this.makeApiRequest('DELETE', `/v2/title-configs/${id}`);
    } catch (err) {
      this.logger.error({ err, id }, 'Error deleting title config');
      throw err;
    }
  }

  // POST creates (categoryVariantId in the body); passing `categoryVariantId`
  // PATCHes that specific variant row.
  async adminUpsertTitleVariantConfig(
    configId: string,
    body: {
      categoryVariantId?: string;
      hidden?: boolean | null;
      sequence?: number | null;
      delimiter?: string | null;
      displayValueFormat?: string | null;
      hiddenValueIds?: string[] | null;
    },
    categoryVariantId?: string,
  ): Promise<TitleVariantConfigT> {
    try {
      return categoryVariantId
        ? await this.makeApiRequest(
            'PATCH',
            `/v2/title-configs/${configId}/variants/${categoryVariantId}`,
            body,
          )
        : await this.makeApiRequest(
            'POST',
            `/v2/title-configs/${configId}/variants`,
            body,
          );
    } catch (err) {
      this.logger.error(
        { err, configId },
        'Error upserting title variant config',
      );
      throw err;
    }
  }

  async adminDeleteTitleVariantConfig(
    configId: string,
    categoryVariantId: string,
  ): Promise<void> {
    try {
      await this.makeApiRequest(
        'DELETE',
        `/v2/title-configs/${configId}/variants/${categoryVariantId}`,
      );
    } catch (err) {
      this.logger.error(
        { err, configId, categoryVariantId },
        'Error deleting title variant config',
      );
      throw err;
    }
  }

  // ---------------------------------------------------------------------------------
  // Admin product editor.
  //
  // Every write returns the whole refreshed AdminProductDetailT, so the page re-renders from
  // the server rather than reconciling its own optimistic guess. The child-collection
  // endpoints take exactly one of create / update / delete / move per call — batching
  // several edits into one request is not possible, by design.
  // ---------------------------------------------------------------------------------

  // The editor's read. Not the public product: this one carries every child row's id,
  // which the commands below target, and filters nothing by `hidden`.
  // `store` selects whose name and description come back — both are store-scoped
  // overrides on top of the base columns.
  async adminGetProduct(
    productId: string,
    store?: string,
  ): Promise<AdminProductDetailT> {
    try {
      return await this.makeApiRequest(
        'GET',
        `/v1/admin/products/${productId}`,
        {},
        store ? { store } : {},
      );
    } catch (err) {
      this.logger.error({ err, productId }, 'Error fetching admin product');
      throw err;
    }
  }

  // Scalars plus both images, all in one call — the only admin write that batches.
  // `store` routes name and description onto that store's row instead of the base
  // columns, which is where the storefront reads them from first.
  async adminUpdateProduct(
    productId: string,
    update: AdminProductUpdateT,
    store?: string,
    actor?: string,
  ): Promise<AdminProductDetailT> {
    try {
      return await this.makeApiRequest(
        'PATCH',
        `/v1/admin/products/${productId}`,
        update,
        store ? { store } : {},
        { actor },
      );
    } catch (err) {
      this.logger.error({ err, productId }, 'Error updating admin product');
      throw err;
    }
  }

  async adminUpdateProductSpecifications(
    productId: string,
    command: AdminSpecificationsCommandT,
    actor?: string,
  ): Promise<AdminProductDetailT> {
    try {
      return await this.makeApiRequest(
        'PATCH',
        `/v1/admin/products/${productId}/specifications`,
        command,
        {},
        { actor },
      );
    } catch (err) {
      this.logger.error(
        { err, productId },
        'Error applying a product specification command',
      );
      throw err;
    }
  }

  async adminUpdateProductFeatures(
    productId: string,
    command: AdminFeaturesCommandT,
    actor?: string,
  ): Promise<AdminProductDetailT> {
    try {
      return await this.makeApiRequest(
        'PATCH',
        `/v1/admin/products/${productId}/features`,
        command,
        {},
        { actor },
      );
    } catch (err) {
      this.logger.error(
        { err, productId },
        'Error applying a product feature command',
      );
      throw err;
    }
  }

  async adminUpdateProductFaqs(
    productId: string,
    command: AdminFaqsCommandT,
    actor?: string,
  ): Promise<AdminProductDetailT> {
    try {
      return await this.makeApiRequest(
        'PATCH',
        `/v1/admin/products/${productId}/faqs`,
        command,
        {},
        { actor },
      );
    } catch (err) {
      this.logger.error(
        { err, productId },
        'Error applying a product faq command',
      );
      throw err;
    }
  }

  /**
   * A product's configurations and the identifiers they carry.
   *
   * Its own endpoint rather than part of the product read: it groups every live SKU of
   * the product, and the largest carry tens of thousands. Defaults to incomplete
   * configurations — nothing backfills a ragged one, so this is where they surface.
   */
  async adminGetProductIdentifiers(
    productId: string,
    query: {
      limit?: number;
      offset?: number;
      incompleteOnly?: boolean;
      variantValue?: string;
    } = {},
  ): Promise<{ items: ProductConfigurationT[]; total: number }> {
    try {
      return await this.makeApiRequest(
        'GET',
        `/v1/admin/products/${productId}/identifiers`,
        {},
        query,
      );
    } catch (err) {
      this.logger.error(
        { err, productId, query },
        'Error fetching product configurations',
      );
      throw err;
    }
  }

  async adminUpdateProductIdentifiers(
    productId: string,
    command: AdminIdentifiersCommandT,
  ): Promise<AdminIdentifiersResultT> {
    try {
      return await this.makeApiRequest(
        'PATCH',
        `/v1/admin/products/${productId}/identifiers`,
        command,
      );
    } catch (err) {
      this.logger.error(
        { err, productId },
        'Error applying a product identifier command',
      );
      throw err;
    }
  }

  /**
   * The product's "Compare Prices" rows: a list/compare-at price per (storage, store),
   * resolved back from the per-SKU `SkuStore` rows a write fans out to.
   */
  async adminGetProductComparePrices(
    productId: string,
  ): Promise<{ items: ComparePriceRowT[] }> {
    try {
      return await this.makeApiRequest(
        'GET',
        `/v1/admin/products/${productId}/compare-prices`,
      );
    } catch (err) {
      this.logger.error(
        { err, productId },
        'Error fetching product compare prices',
      );
      throw err;
    }
  }

  async adminUpdateProductComparePrices(
    productId: string,
    command: AdminComparePricesCommandT,
  ): Promise<{ items: ComparePriceRowT[] }> {
    try {
      return await this.makeApiRequest(
        'PATCH',
        `/v1/admin/products/${productId}/compare-prices`,
        command,
      );
    } catch (err) {
      this.logger.error(
        { err, productId },
        'Error applying a product compare price command',
      );
      throw err;
    }
  }

  /**
   * Products this product is compatible with. The link is symmetric and the service
   * writes it as two mirrored rows, so linking or unlinking from either product reaches
   * both sides in one call.
   */
  async adminGetProductCompatibility(
    productId: string,
  ): Promise<{ items: CompatibleProductT[] }> {
    try {
      return await this.makeApiRequest(
        'GET',
        `/v1/admin/products/${productId}/compatibility`,
      );
    } catch (err) {
      this.logger.error(
        { err, productId },
        'Error fetching product compatibility',
      );
      throw err;
    }
  }

  async adminLinkProductCompatibility(
    productId: string,
    compatibleProductId: string,
    actor?: string,
  ): Promise<{ items: CompatibleProductT[] }> {
    try {
      return await this.makeApiRequest(
        'POST',
        `/v1/admin/products/${productId}/compatibility`,
        { compatibleProductId },
        {},
        { actor },
      );
    } catch (err) {
      this.logger.error(
        { err, productId, compatibleProductId },
        'Error linking a compatible product',
      );
      throw err;
    }
  }

  async adminUnlinkProductCompatibility(
    productId: string,
    compatibleProductId: string,
    actor?: string,
  ): Promise<{ items: CompatibleProductT[] }> {
    try {
      return await this.makeApiRequest(
        'DELETE',
        `/v1/admin/products/${productId}/compatibility/${compatibleProductId}`,
        {},
        {},
        { actor },
      );
    } catch (err) {
      this.logger.error(
        { err, productId, compatibleProductId },
        'Error unlinking a compatible product',
      );
      throw err;
    }
  }

  /**
   * RT-17618 bulk compatibility upload: presigned S3 PUT + async job, same pattern as
   * the product export job -- poll `adminGetProductCompatibilityUpload` for completion.
   */
  async adminCreateProductCompatibilityUpload(body: {
    originalFileName?: string;
    uploadedBy?: string;
  }): Promise<{ uploadId: string; url: string }> {
    try {
      return await this.makeApiRequest(
        'POST',
        '/v1/exports/products/compatibility-uploads',
        body,
      );
    } catch (err) {
      this.logger.error(
        { err, body },
        'Error creating product compatibility upload',
      );
      throw err;
    }
  }

  async adminGetProductCompatibilityUpload(
    id: string,
  ): Promise<ProductCompatibilityUploadT> {
    try {
      return await this.makeApiRequest(
        'GET',
        `/v1/exports/products/compatibility-uploads/${id}`,
      );
    } catch (err) {
      this.logger.error(
        { err, id },
        'Error getting product compatibility upload',
      );
      throw err;
    }
  }

  /**
   * RT-17725 attribute-based inventory upload: a vendor uploads a file naming a
   * product by Model + variant values instead of a Reebelo SKU ID. Same
   * presigned-upload + poll shape as the product compatibility upload above.
   */
  async createInventoryUpload(body: {
    store: ReebeloStoreT;
    vendorId: string;
    originalFileName?: string;
  }): Promise<CreateInventoryUploadResultT> {
    try {
      return await this.makeApiRequest('POST', '/v1/inventory-uploads', body);
    } catch (err) {
      this.logger.error({ err, body }, 'Error creating inventory upload');
      throw err;
    }
  }

  async getInventoryUpload(id: string): Promise<InventoryUploadT> {
    try {
      return await this.makeApiRequest('GET', `/v1/inventory-uploads/${id}`);
    } catch (err) {
      this.logger.error({ err, id }, 'Error getting inventory upload');
      throw err;
    }
  }

  async searchInventoryUploads(
    query: SearchInventoryUploadsParamsT,
  ): Promise<SearchInventoryUploadsResultT> {
    try {
      return await this.makeApiRequest(
        'GET',
        '/v1/inventory-uploads',
        {},
        query,
      );
    } catch (err) {
      this.logger.error({ err, query }, 'Error searching inventory uploads');
      throw err;
    }
  }

  async getInventoryUploadLines(
    id: string,
    query: SearchInventoryUploadLinesParamsT,
  ): Promise<SearchInventoryUploadLinesResultT> {
    try {
      return await this.makeApiRequest(
        'GET',
        `/v1/inventory-uploads/${id}/lines`,
        {},
        query,
      );
    } catch (err) {
      this.logger.error(
        { err, id, query },
        'Error getting inventory upload lines',
      );
      throw err;
    }
  }

  // The underlying MsCatalog2 route streams back the CSV file itself (with a
  // Content-Disposition header) rather than JSON; makeApiRequest's axios call
  // receives that as a plain string since it fails to parse as JSON.
  async getInventoryUploadReport(id: string): Promise<string> {
    try {
      return await this.makeApiRequest(
        'GET',
        `/v1/inventory-uploads/${id}/report`,
      );
    } catch (err) {
      this.logger.error({ err, id }, 'Error getting inventory upload report');
      throw err;
    }
  }

  async adminUpdateProductVariants(
    productId: string,
    command: AdminProductVariantsCommandT,
    actor?: string,
  ): Promise<AdminProductDetailT> {
    try {
      return await this.makeApiRequest(
        'PATCH',
        `/v1/admin/products/${productId}/variants`,
        command,
        {},
        { actor },
      );
    } catch (err) {
      this.logger.error(
        { err, productId },
        'Error applying a product variant command',
      );
      throw err;
    }
  }

  async adminUpdateProductVariantValues(
    productId: string,
    variantId: string,
    command: AdminProductVariantValuesCommandT,
    actor?: string,
  ): Promise<AdminProductDetailT> {
    try {
      return await this.makeApiRequest(
        'PATCH',
        `/v1/admin/products/${productId}/variants/${variantId}/values`,
        command,
        {},
        { actor },
      );
    } catch (err) {
      this.logger.error(
        { err, productId, variantId },
        'Error applying a product variant value command',
      );
      throw err;
    }
  }

  // Colour values only — the backend refuses any other variant.
  async adminUpdateValueImages(
    productId: string,
    variantId: string,
    valueId: string,
    command: AdminValueImagesCommandT,
    actor?: string,
  ): Promise<AdminProductDetailT> {
    try {
      return await this.makeApiRequest(
        'PATCH',
        `/v1/admin/products/${productId}/variants/${variantId}/values/${valueId}/images`,
        command,
        {},
        { actor },
      );
    } catch (err) {
      this.logger.error(
        { err, productId, variantId, valueId },
        'Error applying a value image command',
      );
      throw err;
    }
  }

  /**
   * The product's edit history, newest first.
   *
   * Starts where the V2 trail does. A product edited only through V1 comes back empty,
   * and that history stays in the Elasticsearch index the classic page reads.
   */
  async adminGetProductAuditEvents(
    productId: string,
    query: { limit?: number; offset?: number } = {},
  ): Promise<AdminProductAuditEventsT> {
    try {
      return await this.makeApiRequest(
        'GET',
        `/v1/admin/products/${productId}/audit-events`,
        {},
        query,
      );
    } catch (err) {
      this.logger.error(
        { err, productId, query },
        'Error fetching product audit events',
      );
      throw err;
    }
  }

  // `parentId` is a reebeloId; omitted creates a root category.
  async adminCreateCategory(input: {
    name: string;
    parentId?: string;
    description?: string;
  }): Promise<CategoryT> {
    try {
      return await this.makeApiRequest('POST', '/v1/admin/categories', input);
    } catch (err) {
      this.logger.error({ err, input }, 'Error creating category');
      throw err;
    }
  }

  // reebeloId only — the route resolves through fetchOneByReebeloId, so a legacy pk 404s.
  // `confirm` carries through the 409 the first call returns when the product still has
  // SKUs. Offers are refused outright and no confirm gets past that.
  async adminDeleteProduct(productId: string, confirm = false): Promise<void> {
    try {
      await this.makeApiRequest(
        'DELETE',
        `/v1/admin/products/${productId}`,
        {},
        confirm ? { confirm: 'true' } : {},
      );
    } catch (err) {
      this.logger.error({ err, productId }, 'Error deleting admin product');
      throw err;
    }
  }

  async adminDeleteCategory(
    categoryId: string,
    confirm = false,
  ): Promise<void> {
    try {
      await this.makeApiRequest(
        'DELETE',
        `/v1/admin/categories/${categoryId}`,
        {},
        confirm ? { confirm: 'true' } : {},
      );
    } catch (err) {
      this.logger.error({ err, categoryId }, 'Error deleting category');
      throw err;
    }
  }

  // Make API Request
  private async makeApiRequest<T>(
    method: Method,
    route: string,
    body = {},
    query = {},
    options: {
      signal?: AbortSignal;
      timeout?: number;
      paramsSerializer?: AxiosRequestConfig['paramsSerializer'];
      // Statuses this route treats as an answer rather than a failure. Logged at debug
      // and still thrown, so callers are unaffected — only the log level changes.
      expectedStatuses?: number[];
      // Who is making the change, for MsCatalog2's audit trail. Only the admin writes
      // send it; everything else reaches the service with no person behind it.
      actor?: string;
    } = {},
  ) {
    try {
      const url = await this.getUrl(route);
      const { actor, ...axiosOptions } = options;
      const headers = {
        ...(await this.getHeaders()),
        ...(actor ? { 'x-actor-email': actor } : {}),
      };

      if (!url) {
        throw new Error('Error making MsCatalog2 API request - Missing url');
      }

      const payload: AxiosRequestConfig = {
        method,
        url,
        headers,
        params: query,
        data: body,
        ...axiosOptions,
      };

      this.logger.debug({ payload }, 'Making MsCatalog2 API request');

      const { data, status } = await axios(payload);

      this.logger.debug(
        { method, url, body, response: data, statusCode: status },
        'Response from MsCatalog2 API',
      );

      return data as T;
    } catch (err: any) {
      if (axios.isAxiosError(err)) {
        const { response } = err;

        if (response) {
          const context = {
            err,
            method,
            route,
            body,
            status: response.status,
            response: response.data,
          };

          if (options.expectedStatuses?.includes(response.status)) {
            this.logger.debug(
              context,
              'MsCatalog2 API request returned an expected status',
            );
          } else {
            this.logger.error(context, 'Error making MsCatalog2 API request');
          }

          const { error } = response.data;

          if (error) {
            // Carries the upstream status rather than collapsing to 400 — callers
            // distinguish "needs confirmation" from "refused" by it.
            throw new ErrorWithStatus(
              response.status,
              response.data?.message ??
                response.data?.error ??
                'An error has occured. Please try again later.',
            );
          }
        }
      } else {
        this.logger.error(
          { err, method, route, body },
          'Error making MsCatalog2 API request',
        );
      }

      throw err;
    }
  }

  // Get URL
  private async getUrl(path: string): Promise<string | null> {
    const url =
      this.url ||
      process.env.CATALOG_V2_API_URL ||
      (await this.ssm.getStoreValue(this.store, 'CATALOG_V2_API_URL'));

    if (!this.url && url) {
      this.url = url;
    }

    // Without this, an unreadable parameter leaves the base url empty and axios resolves
    // the route against nothing — issuing the request to localhost and failing with
    // EADDRNOTAVAIL, which says nothing about the missing parameter.
    //
    // What is wrong and how to fix it goes to the log. The caller is told which store
    // failed and no more: the response reaches a browser, and the configuration of this
    // service is not its business.
    if (!this.url) {
      this.logger.error(
        { store: this.store, parameter: `/${this.store}/catalog_v2_api_url` },
        'No MsCatalog2 base url: set CATALOG_V2_API_URL, or grant this function the store parameter in SSM',
      );

      throw new Error(`MsCatalog2 is unavailable for ${this.store}`);
    }

    return this.url + path;
  }

  // Get Headers
  private async getHeaders(): Promise<AxiosRequestConfig['headers']> {
    const apiKey =
      this.apiKey ||
      process.env.CATALOG_V2_API_KEY ||
      (await this.ssm.getStoreValue(this.store, 'CATALOG_V2_API_KEY'));

    if (!this.apiKey && apiKey) {
      this.apiKey = apiKey;
    }

    return {
      'x-api-key': apiKey,
    };
  }
}
